import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { RootState } from '@/rootReducer';
import type { Business } from '@/modules/dashboard/dashboardTypes';
import type { EmailTemplate } from '@/modules/email/emailTypes';
import { LeftChevronGold } from '@/assets/images';
import Refresh from '@/assets/images/Reset datepicker.png';
import DatePickerScheduledSendListItem from './DatePickerScheduledSendListItem/DatePickerScheduledSendListItem';
import './DatePickerScheduledSend.scss';

interface Props {
	selectedDateAndTime: Dayjs | null;
	setSelectedDateAndTime: (x: Dayjs | null) => void;
	activeBusiness: Business | undefined;
	selectedTemplate: EmailTemplate;
	hasSentEmail: boolean;
}

const DatePickerScheduledSend = ({
	selectedDateAndTime,
	setSelectedDateAndTime,
	activeBusiness,
	selectedTemplate,
	hasSentEmail
}: Props): JSX.Element => {
	const [
		scheduledEmailActivity,
		scheduledEmailLimits,
		staleTemplates,
		aiText,
		aiLoading,
		aiCredits,
		selectedList
	] = useSelector((state: RootState) => [
		state.email.getIn(['email', 'scheduled', 'activity']).toJS(),
		state.email.getIn(['email', 'scheduled', 'limits']).toJS(),
		state.email.getIn(['templates', 'stale']).toJS(),
		state.email.getIn(['ai', 'text']).toJS(),
		state.email.getIn(['ai', 'loading']),
		state.email.getIn(['ai', 'credits']),
		state.dashboard.getIn(['lists', 'selected']).toJS()
	]);
	const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
	const [calendarCells, setCalendarCells] = useState<Dayjs[]>([]);
	const [timeSlots, setTimeSlots] = useState<Dayjs[]>([]);
	const [showDates, setShowDates] = useState<boolean>(false);
	const [showTimes, setShowTimes] = useState<boolean>(false);
	const datePickerRef = useRef<HTMLDivElement>(null);

	const closeDatePicker = e => {
		if (
			!datePickerRef?.current?.contains(e.target) &&
			!e.target.classList.contains('scheduled-send-button') &&
			!e.target.classList.contains('scheduled-send-list-item-holder') &&
			!e.target.classList.contains('scheduled-send-list-item-weekday') &&
			!e.target.classList.contains('scheduled-send-list-item-date') &&
			!e.target.classList.contains('scheduled-send-list-item-envelope') &&
			!e.target.classList.contains(
				'scheduled-send-list-item-emails-left'
			) &&
			!e.target.classList.contains('scheduled-send-time-slot')
		) {
			setShowDates(false);
			setShowTimes(false);
			setSelectedDate(dayjs());
		}
	};

	useEffect(() => {
		window.addEventListener('click', closeDatePicker);

		return () => window.removeEventListener('click', closeDatePicker);
	}, []);

	useEffect(() => {
		const calendarCellsArr: Dayjs[] = [selectedDate];

		const createCell = (day: number) => {
			return selectedDate.clone().add(day, 'day');
		};

		for (let i = 0; i <= 28; i++) {
			calendarCellsArr.push(createCell(i + 1));
		}

		setCalendarCells(calendarCellsArr);
	}, [selectedDate]);

	useEffect(() => {
		if (showTimes) {
			const timesArr = [];
			let startingTime = selectedDate.set('second', 0);
			const startingTimeMinutes = Number(startingTime.format('m'));

			startingTime = startingTime.set('hour', 0);
			startingTime = startingTime.set('minute', 0);

			const createTimeSlot = (minutes: number) => {
				const timeSlot = startingTime.clone().add(minutes, 'minute');

				if (timeSlot.get('date') > startingTime.get('date')) {
					return timeSlot.set('date', startingTime.get('date'));
				}

				return timeSlot;
			};

			for (let i = 0; i <= 47; i++) {
				timesArr.push(createTimeSlot(i * 30));
			}

			setTimeSlots(timesArr);
		}
	}, [showTimes]);

	const buttonText = () => {
		if (selectedDateAndTime) {
			return selectedDateAndTime.format('ddd, MMM D, hh:mm A');
		}

		return `${dayjs().format('ddd')}, Today`;
	};

	const isDisabled =
		hasSentEmail ||
		scheduledEmailActivity[`${activeBusiness?._id}`] ||
		staleTemplates?.[`${selectedTemplate?.id}`] ||
		((aiLoading || aiCredits <= 0) &&
			!aiText?.[`${selectedList?.id}`]?.[`${selectedTemplate?.id}`]?.[
				`${activeBusiness?._id}`
			] &&
			selectedTemplate?.body?.includes('[ai_snippet]'))
			? 'disabled'
			: '';

	const backButtonHandler = (e: React.MouseEvent) => {
		e.stopPropagation();
		setShowDates(true);
		setShowTimes(false);
		setSelectedDate(dayjs());
	};

	return (
		<>
			<button
				id="scheduled-send-but"
				className={`scheduled-send-button ${isDisabled ? 'disabled' : ''} `}
				disabled={isDisabled}
				onClick={() => {
					if (!showDates && !showTimes) {
						setShowDates(true);
						return;
					}

					if (showDates && !showTimes) {
						setSelectedDate(dayjs());
						setShowDates(false);
						return;
					}

					if (!showDates && showTimes) {
						setSelectedDate(dayjs());
						setSelectedDateAndTime(null);
						setShowTimes(false);
					}
				}}
			>
				{buttonText()}
			</button>
			{(showTimes || showDates) && (
				<div
					className="scheduled-send-date-picker-holder"
					ref={datePickerRef}
				>
					<span className="scheduled-send-date-picker-header">
						{showTimes && (
							<LeftChevronGold
								className="scheduled-send-date-picker-back-button"
								onClick={backButtonHandler}
							/>
						)}
						{showTimes
							? 'Select the time'
							: 'Select the date for scheduling emails'}
					</span>
					{showDates &&
						calendarCells.map((cell: Dayjs) => {
							return (
								<DatePickerScheduledSendListItem
									date={cell}
									setSelectedDate={setSelectedDate}
									selectedDate={selectedDate}
									setShowTimes={setShowTimes}
									setShowDates={setShowDates}
								/>
							);
						})}
					{showTimes &&
						timeSlots.map((slot: Dayjs) => {
							const timeSlotDate = slot?.format('MM-DD-YYYY');
							const timeSlotDateAndTime =
								slot?.format('MM-DD-YYYY-HH-mm');
							const timeDiff = dayjs().diff(slot);

							if (timeDiff > 0) {
								return;
							}

							return (
								<div
									id="scheduled-send-time"
									key={timeSlotDateAndTime}
									className={`scheduled-send-time-slot ${scheduledEmailLimits[timeSlotDate]?.units[timeSlotDateAndTime]?.limit === 0 ? 'disabled' : ''} `}
									onClick={() => {
										if (
											scheduledEmailLimits[timeSlotDate]
												?.units[timeSlotDateAndTime]
												?.limit === 0
										) {
											return;
										}
										setSelectedDate(dayjs());
										setSelectedDateAndTime(slot);
										setShowTimes(false);
									}}
								>
									{slot.format('hh:mm A')}
								</div>
							);
						})}
				</div>
			)}
			<img
				src={Refresh}
				className={`scheduled-send-refresh ${
					scheduledEmailActivity[`${activeBusiness?._id}`] ||
					staleTemplates?.[`${selectedTemplate?.id}`] ||
					((aiLoading || aiCredits <= 0) &&
						!aiText?.[`${selectedList?.id}`]?.[
							`${selectedTemplate?.id}`
						]?.[`${activeBusiness?._id}`] &&
						selectedTemplate?.body?.includes('[ai_snippet]'))
						? 'disabled'
						: ''
				} `}
				onClick={() => {
					if (
						scheduledEmailActivity[`${activeBusiness?._id}`] ||
						staleTemplates?.[`${selectedTemplate?.id}`] ||
						((aiLoading || aiCredits <= 0) &&
							!aiText?.[`${selectedList?.id}`]?.[
								`${selectedTemplate?.id}`
							]?.[`${activeBusiness?._id}`] &&
							selectedTemplate?.body?.includes('[ai_snippet]'))
					) {
						return;
					}

					setSelectedDate(dayjs());

					if (selectedDateAndTime) {
						setSelectedDateAndTime(null);
					}

					if (showDates) {
						setShowDates(false);
					}

					if (showTimes) {
						setShowTimes(false);
					}
				}}
			/>
		</>
	);
};

export default DatePickerScheduledSend;
